import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useRef, useState } from 'react';
import { demeterDashboardsApi, demeterUsersApi } from '../../../Apis/Apis';
import promiseHandler from '../../../Core/Handlers/PromiseHandler';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    CloneDemeterDashboardRequest,
    DeleteDemeterDashboardRequest,
    DemeterDashboardModel,
    DemeterPageElementModel,
    DemeterPageElementType,
    DemeterPageType,
    DemeterRegion,
    DemeterTableDefinitionType,
    DemeterUserStoreType,
    UpsertDemeterDashboardRequest,
} from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserMarkets } from '../../../Redux/Slices/UserSlice';
import useApi from '../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import usePageElementsApi from '../../Apis/Hooks/usePageElementsApiHook';
import useUserStoreApi from '../../Apis/Hooks/useUserStoreApiHook';
import IconButton from '../../Components/Form/Buttons/IconButton';
import Switch from '../../Components/Form/Buttons/Switch';
import TextButton from '../../Components/Form/Buttons/TextButton';
import Dropdown from '../../Components/Form/Inputs/Dropdown';
import IconMenuHorizontalDropdown, { IIconMenuHorizontalDropdownOption } from '../../Components/Form/Inputs/IconMenuHorizontalDropdown';
import TextInput from '../../Components/Form/Inputs/TextInput';
import PageHeader from '../../Components/Headers/PageHeader';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import ConfirmModal from '../../Components/Modals/ConfirmModal/ConfirmModal';
import MenuSettingsModal from '../../Components/Modals/ProductsModal/MenuSettingsModal';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import InlineTab from '../../Components/Navigation/InlineTabs/InlineTab';
import InlineTabs from '../../Components/Navigation/InlineTabs/InlineTabs';
import RegexValidators from '../../Core/Validation/RegexValidators';
import { Language, languageOptions } from '../../Services/Language/LanguageService';
import useLanguage from '../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum } from '../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../Services/Logging/LoggingService';
import DashboardContainer from './DashboardContainer';
import styles from './DashboardCustomizationPage.module.scss';
import dashboardCustomizationService from './DashboardCustomizationService';
import useGlobalLeadingIndicators from './Hooks/useGlobalLeadingIndicatorsHook';

const maximumNumberOfDashboards = 10;
let previouslySelectedDashboardTabGuid = '';

const DashboardCustomizationPage: React.FC = () => {
    // Application hooks.
    const [translations, , language, setLanguage] = useLanguage();
    const [searchParameters] = useSearchParameters();
    const [isMenuSettingsModalOpen, setIsMenuSettingsModalOpen] = useState(false);
    const [userStoreValue] = useUserStoreApi(DemeterUserStoreType.DisplaySettings);
    const listPageElements = usePageElementsApi({
        isPageElementExcluded: (pageElement: DemeterPageElementModel) => {
            if (
                pageElement.pageType === DemeterPageType.CommodityIndicators ||
                pageElement.pageType === DemeterPageType.HedgeMonitorPro ||
                pageElement.pageType === DemeterPageType.Presentation ||
                pageElement.pageType === DemeterPageType.ValueMatrixCalculator
            ) {
                return true;
            }

            if (
                pageElement.pageElementType === DemeterPageElementType.FuturesPricesForwardCurveChart ||
                pageElement.pageElementType === DemeterPageElementType.OtcPricesForwardCurveChart
            ) {
                return true;
            }

            if (
                (pageElement.pageType === DemeterPageType.Exports ||
                    pageElement.pageType === DemeterPageType.Production ||
                    pageElement.pageType === DemeterPageType.Imports) &&
                pageElement.region === DemeterRegion.TopThirty
            ) {
                return true;
            }

            return false;
        },
    });
    // DragAndDrop
    const [isInEditMode, setIsInEditMode] = useState(false);
    const [dashboards, setDashboards] = useState<DemeterDashboardModel[]>([]);
    const [openConfirmDeleteDashboardModal, setOpenConfirmDeleteDashboardModal] = useState(false);
    const [openConfirmResetDashboardModal, setOpenConfirmResetDashboardModal] = useState(false);
    const [deleteDemeterDashboardRequest, setDeleteDemeterDashboardRequest] = useState<DeleteDemeterDashboardRequest>();
    const [dashboardNameEdits, setDashboardNameEdits] = useState<Record<string, string>>({});
    // Prevents flickering in between an upsert call and a get dashboard list call on conditions where dashboard requires fresh data.
    const [triggerRefreshListDemeterDashboardsResponse, setTriggerRefreshListDemeterDashboardsResponse] = useState(false);
    const [activeDashboardTab, setActiveDashboardTab] = useState<string>('');
    const upsertDemeterDashboardRequest = useRef<UpsertDemeterDashboardRequest | null>(null);
    const shouldFocusNewDashboard = useRef(false);
    const markets = useApplicationSelector(selectUserMarkets);
    const { initializeGlobalData, isGlobalDataInitialized } = useGlobalLeadingIndicators();
    const [isListDemeterDashboardsResponseLoading, refreshListDemeterDashboardsResponse, listDemeterDashboardsResponse] = useApi(() =>
        demeterDashboardsApi.listDemeterDashboards(true),
    );

    const [isUpsertDemeterDashboardLoading, upsertDemeterDashboard, upsertDemeterDashboardResponse] = useApiWithoutAutoExecute(
        (request: UpsertDemeterDashboardRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterDashboardsApi.upsertDemeterDashboard(request);
        },
    );

    const [, deleteDemeterDashboard] = useApiWithoutAutoExecute(
        (request: DeleteDemeterDashboardRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterDashboardsApi.deleteDemeterDashboard(request);
        },
        {
            successMessage: translations.dashboardCustomization.messages.deleteDashboardSuccess,
            errorMessage: translations.dashboardCustomization.messages.deleteDashboardError,
        },
    );

    const [isCloneDemeterDashboardLoading, cloneDemeterDashboard, cloneDemeterDashboardResponse] = useApiWithoutAutoExecute(
        (request: CloneDemeterDashboardRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterDashboardsApi.cloneDemeterDashboard(request);
        },
    );

    const handleDeleteDashboardTabButtonClicked = (dashboard: DemeterDashboardModel) => {
        setDeleteDemeterDashboardRequest({ demeterDashboardGuid: dashboard.demeterDashboardGuid });
        setOpenConfirmDeleteDashboardModal(true);
    };

    const confirmDeleteDashboard = () => {
        if (!deleteDemeterDashboardRequest) {
            return;
        }

        if (dashboards.length === 1) {
            const newDashboard = dashboardCustomizationService.getNewEmptyDashboard(translations.dashboardCustomization.titles.dashboard);
            newDashboard.demeterDashboardGuid = deleteDemeterDashboardRequest.demeterDashboardGuid;
            updateUpsertDashboardRequest(newDashboard);
        } else {
            if (searchParameters.dashboardTab === deleteDemeterDashboardRequest.demeterDashboardGuid) {
                setActiveDashboardTab(dashboards[0]?.demeterDashboardGuid || '');
            }
            setDashboards((previous) => previous.filter((x) => x.demeterDashboardGuid !== deleteDemeterDashboardRequest.demeterDashboardGuid));
            deleteDemeterDashboard(deleteDemeterDashboardRequest);
        }

        setDeleteDemeterDashboardRequest(undefined);
        setOpenConfirmDeleteDashboardModal(false);
    };

    const handleResetToDefaultDashboardButtonClicked = () => {
        const currentDashboard = dashboards.find((x) => x.demeterDashboardGuid === searchParameters.dashboardTab);
        if (!upsertDemeterDashboardRequest.current && !currentDashboard?.rows.length) {
            confirmResetDashboard();
            return;
        }

        setOpenConfirmResetDashboardModal(true);
    };

    const confirmResetDashboard = () => {
        const currentDashboard = dashboards.find((x) => x.demeterDashboardGuid === searchParameters.dashboardTab);
        if (!currentDashboard) {
            return;
        }

        const resetDashboard = dashboardCustomizationService.getNewDefaultDashboard(
            translations.dashboardCustomization.titles.dashboard,
            pageElements,
            markets,
            currentDashboard.order,
            currentDashboard.demeterDashboardGuid,
        );
        setTriggerRefreshListDemeterDashboardsResponse(true);
        upsertDemeterDashboard(resetDashboard);
        setOpenConfirmResetDashboardModal(false);
    };

    const handleAddNewEmptyDashboard = () => {
        const newDashboardOrder = dashboards.length;
        const newDashboard = dashboardCustomizationService.getNewEmptyDashboard(translations.dashboardCustomization.titles.dashboard, newDashboardOrder);
        setDashboards((previous) => [...previous, newDashboard]);
        const upsertDashboardRequest: UpsertDemeterDashboardRequest = {
            ...newDashboard,
        };
        shouldFocusNewDashboard.current = true;
        setTriggerRefreshListDemeterDashboardsResponse(true);
        upsertDemeterDashboard(upsertDashboardRequest);
    };

    const handleDuplicateDashboard = () => {
        const currentDashboard = dashboards.find((x) => x.demeterDashboardGuid === searchParameters.dashboardTab);
        if (!currentDashboard) {
            return;
        }

        // Extract base name by removing any existing (n) pattern at the end
        const baseName = currentDashboard.name.match(RegexValidators.BaseNameAndIterationCount)?.[1].trim() ?? currentDashboard.name;

        // Find all dashboards with the same base name
        const relatedDashboards = dashboards.filter((dashboard) => {
            const dashboardBaseName = dashboard.name.match(RegexValidators.BaseNameAndIterationCount)?.[1].trim() ?? dashboard.name;
            return dashboardBaseName === baseName;
        });

        // Find the highest iteration number (starting from 1 for the original)
        let maxIteration = 1;
        relatedDashboards.forEach((dashboard) => {
            const match = dashboard.name.match(RegexValidators.IterationCount);
            if (match) {
                const iteration = parseInt(match[1], 10);
                if (iteration > maxIteration) {
                    maxIteration = iteration;
                }
            }
        });

        const newIterationNumber = maxIteration + 1;
        const newDashboardName = `${baseName} (${newIterationNumber})`;
        const cloneDashboardRequest: CloneDemeterDashboardRequest = {
            demeterDashboardGuid: currentDashboard.demeterDashboardGuid,
            name: newDashboardName,
        };

        shouldFocusNewDashboard.current = true;
        setTriggerRefreshListDemeterDashboardsResponse(true);
        cloneDemeterDashboard(cloneDashboardRequest);
    };

    const handleAddDefaultDashboard = () => {
        const newDashboardOrder = dashboards.length;
        const defaultDashboard = dashboardCustomizationService.getNewDefaultDashboard(
            translations.dashboardCustomization.titles.dashboard,
            pageElements,
            markets,
            newDashboardOrder,
        );
        const upsertDashboardRequest: UpsertDemeterDashboardRequest = {
            ...defaultDashboard,
        };
        shouldFocusNewDashboard.current = true;
        setTriggerRefreshListDemeterDashboardsResponse(true);
        upsertDemeterDashboard(upsertDashboardRequest);
    };

    const addDropdownOptions: IIconMenuHorizontalDropdownOption[] = [
        {
            key: 'addNew',
            label: translations.dashboardCustomization.labels.addNew,
            handleClick: handleAddNewEmptyDashboard,
        },
        {
            key: 'duplicate',
            label: translations.dashboardCustomization.labels.duplicate,
            handleClick: handleDuplicateDashboard,
        },
        {
            key: 'default',
            label: translations.dashboardCustomization.labels.default,
            handleClick: handleAddDefaultDashboard,
        },
    ];

    const handleDashboardLabelLoseFocus = (dashboardGuid: string, value: string) => {
        const currentDashboard = dashboards.find((x) => x.demeterDashboardGuid === dashboardGuid);
        if (!currentDashboard) {
            return;
        }

        if (value === currentDashboard.name) {
            return;
        }

        const updatedDashboard = {
            ...currentDashboard,
            name: value,
        };
        updateUpsertDashboardRequest(updatedDashboard);
    };

    const updateUpsertDashboardRequest = (upsertDashboard: DemeterDashboardModel) => {
        const upsertDashboardRequest: UpsertDemeterDashboardRequest = {
            ...upsertDashboard,
        };

        upsertDashboardRequest.rows = upsertDashboardRequest.rows?.filter((x) => x.rows.length > 0);
        setDashboards((previous) =>
            previous.map((dashboard) => {
                if (dashboard.demeterDashboardGuid === upsertDashboardRequest.demeterDashboardGuid) {
                    return upsertDashboard;
                }

                return dashboard;
            }),
        );
        upsertDemeterDashboard(upsertDashboardRequest);
    };

    const updateUpsertDashboardRequestFromContainer = () => {
        if (!upsertDemeterDashboardRequest.current) {
            return;
        }

        upsertDemeterDashboard(upsertDemeterDashboardRequest.current);
        setTriggerRefreshListDemeterDashboardsResponse(true);
    };

    const setUpsertDemeterDashboardRequest = (upsertDashboard: UpsertDemeterDashboardRequest) => {
        upsertDemeterDashboardRequest.current = upsertDashboard;
    };

    const handleLanguageChange = async (newLanguage: Language, isOnLoad: boolean = false) => {
        await setLanguage(newLanguage);
        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.LanguageChange, newLanguage);
        if (!isOnLoad) {
            await promiseHandler(
                demeterUsersApi.updateUserStore(DemeterUserStoreType.LanguageSelected, {
                    userStoreType: DemeterUserStoreType.LocationData,
                    value: { language: newLanguage as string },
                }),
            );
        }
    };

    const handleIsMenuSettingsModalOpen = () => {
        setIsMenuSettingsModalOpen((previous) => !previous);
    };

    const pageElements = useMemo(() => {
        if (!listPageElements) {
            return [];
        }

        return listPageElements;
    }, [listPageElements]);

    const pageTitle = useMemo(() => {
        const currentDashboardName =
            dashboardNameEdits[searchParameters.dashboardTab] ?? dashboards?.find((x) => x.demeterDashboardGuid === searchParameters.dashboardTab)?.name;

        if (!currentDashboardName) {
            return translations.dashboardCustomization.titles.dashboard;
        }

        return currentDashboardName;
    }, [dashboards, searchParameters.dashboardTab, dashboardNameEdits, translations, language]);

    useEffect(() => {
        if (isListDemeterDashboardsResponseLoading || !listDemeterDashboardsResponse || !pageElements?.length) {
            return;
        }

        if (!listDemeterDashboardsResponse.rows?.length) {
            const newDashboard = dashboardCustomizationService.getNewDefaultDashboard(
                translations.dashboardCustomization.titles.dashboard,
                pageElements,
                markets,
            );
            shouldFocusNewDashboard.current = true;
            setTriggerRefreshListDemeterDashboardsResponse(true);
            updateUpsertDashboardRequest(newDashboard);
            return;
        }

        if (shouldFocusNewDashboard.current && listDemeterDashboardsResponse.rows?.length) {
            const dashboardToFocus = listDemeterDashboardsResponse.rows[listDemeterDashboardsResponse.rows.length - 1];
            setActiveDashboardTab(dashboardToFocus.demeterDashboardGuid);
            shouldFocusNewDashboard.current = false;
        }

        setDashboards([
            ...listDemeterDashboardsResponse.rows
                .map((dashboard) => ({
                    ...dashboard,
                    rows: dashboard.rows
                        .map((row) => {
                            row.rows = row.rows.sort((a, b) => a.order - b.order);
                            return row;
                        })
                        .sort((a, b) => a.order - b.order),
                }))
                .sort((a, b) => a.order - b.order),
        ]);
    }, [listDemeterDashboardsResponse?.rows, isListDemeterDashboardsResponseLoading, pageElements]);

    useEffect(() => {
        if (activeDashboardTab && searchParameters.dashboardTab === activeDashboardTab) {
            setActiveDashboardTab('');
        }
    }, [searchParameters.dashboardTab, activeDashboardTab]);

    useEffect(() => {
        if (searchParameters.dashboardTab) {
            previouslySelectedDashboardTabGuid = searchParameters.dashboardTab;
        }
    }, [searchParameters.dashboardTab]);

    useEffect(() => {
        if (isUpsertDemeterDashboardLoading || !triggerRefreshListDemeterDashboardsResponse || !upsertDemeterDashboardResponse) {
            return;
        }

        setTriggerRefreshListDemeterDashboardsResponse(false);
        upsertDemeterDashboardRequest.current = null;
        refreshListDemeterDashboardsResponse();
    }, [isUpsertDemeterDashboardLoading, upsertDemeterDashboardResponse]);

    useEffect(() => {
        if (isCloneDemeterDashboardLoading || !triggerRefreshListDemeterDashboardsResponse || !cloneDemeterDashboardResponse) {
            return;
        }

        setTriggerRefreshListDemeterDashboardsResponse(false);
        refreshListDemeterDashboardsResponse();
    }, [isCloneDemeterDashboardLoading, cloneDemeterDashboardResponse]);

    useEffect(() => {
        if (!userStoreValue?.userStore?.value) {
            return;
        }

        setIsMenuSettingsModalOpen(userStoreValue?.userStore?.value[applicationConstants.MenuSettingsUserAlreadyAcknowledged] !== true);
    }, [userStoreValue]);

    useEffect(
        () => () => {
            if (upsertDemeterDashboardRequest.current) {
                // Upsert any pending changes on unMount (page change)
                // Call api instead of refresh hook to avoid closure vs unMount issues
                demeterDashboardsApi.upsertDemeterDashboard(upsertDemeterDashboardRequest.current);
            }

            upsertDemeterDashboardRequest.current = null;
        },
        [],
    );

    useEffect(() => {
        const hasLeadingIndicators = pageElements.some((element) => element.pageElementType === DemeterPageElementType.LeadingIndicators);

        if (hasLeadingIndicators && markets.length > 0 && !isGlobalDataInitialized) {
            initializeGlobalData(markets);
        }
    }, [pageElements, markets, initializeGlobalData, isGlobalDataInitialized]);

    return (
        <div className={styles.master_page_container}>
            <div className={styles.indicators_dashboard_section_container}>
                <PageHeader
                    title={pageTitle}
                    rightSideComponent={
                        <div className={styles.indicators_dropdowns_container}>
                            <div className={styles.indicators_dropdowns}>
                                <Dropdown
                                    value={languageOptions.find((x) => x.value === language)?.value ?? languageOptions[0].value}
                                    options={languageOptions}
                                    handleOptionChange={handleLanguageChange}
                                    track={{ eventCategory: 'Dashboard', eventAction: 'LanguageChanged' }}
                                />
                            </div>
                        </div>
                    }
                    testId="DashboardPageHeader"
                />
                {(!dashboards.length || !pageElements.length) && <CardSkeleton />}
                {dashboards && !!pageElements.length && (
                    <div className={styles.dashboard_customization_tabs_container}>
                        <div className={styles.dashboard_customization_inline_tabs} data-testid="CustomDashboardInlineTabsContainer">
                            <InlineTabs
                                inlineTabsKey="dashboardTab"
                                activeTab={activeDashboardTab}
                                defaultTab={previouslySelectedDashboardTabGuid}
                                testId="customDashboardInlineTabs"
                                tabsRightSide={
                                    <div className={styles.dashboard_customization_actions_container}>
                                        <div>
                                            {isInEditMode && (
                                                <IconMenuHorizontalDropdown
                                                    icon={<AddIcon fontSize="small" />}
                                                    options={addDropdownOptions}
                                                    tooltip={
                                                        dashboards.length >= maximumNumberOfDashboards
                                                            ? translations.dashboardCustomization.tooltips.maximumMessage
                                                            : translations.dashboardCustomization.tooltips.addNewDashboard
                                                    }
                                                    disabled={dashboards.length >= maximumNumberOfDashboards}
                                                    className={
                                                        dashboards.length >= maximumNumberOfDashboards
                                                            ? styles.dashboard_customization_tab_icon_button_disabled
                                                            : styles.dashboard_customization_tab_icon_button
                                                    }
                                                    menuClassName={styles.dashboard_customization_add_dropdown_menu}
                                                    itemClassName={styles.dashboard_customization_add_dropdown_item}
                                                    track={{ eventCategory: 'Dashboard', eventAction: 'AddDashboardClicked' }}
                                                />
                                            )}
                                        </div>
                                        <div className={styles.dashboard_customization_actions_right}>
                                            <div
                                                className={styles.dashboard_customization_actions_right_buttons}
                                                data-testid="CustomDashboardActionsRightButtons"
                                            >
                                                {isInEditMode && (
                                                    <TextButton
                                                        title={translations.dashboardCustomization.titles.resetDashboard}
                                                        onClick={() => handleResetToDefaultDashboardButtonClicked()}
                                                        track={{ eventCategory: 'Dashboard', eventAction: 'ResetDashboardClicked' }}
                                                    />
                                                )}
                                                <Switch
                                                    handleChange={(value) => {
                                                        setIsInEditMode(value);
                                                        setDashboardNameEdits({});
                                                        setDeleteDemeterDashboardRequest(undefined);
                                                        refreshListDemeterDashboardsResponse();
                                                    }}
                                                    disabled={isUpsertDemeterDashboardLoading}
                                                    track={{
                                                        eventCategory: 'Dashboard',
                                                        eventAction: 'EditModeChanged',
                                                    }}
                                                />
                                                <span className={styles.dashboard_customization_actions_toggle_label}>
                                                    {translations.dashboardCustomization.text.editView}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                }
                                track={{ eventCategory: 'Dashboard', eventAction: 'Tab' }}
                            >
                                {dashboards.map((dashboard, index) => (
                                    <InlineTab
                                        show
                                        key={dashboard.demeterDashboardGuid}
                                        testId={`CustomDashboardTab-${dashboard.demeterDashboardGuid}`}
                                        title={
                                            !isInEditMode || searchParameters.dashboardTab !== dashboard.demeterDashboardGuid ? (
                                                dashboard.name
                                            ) : (
                                                <TextInput
                                                    testId={`dashboardNameInput${index}`}
                                                    value={dashboardNameEdits[dashboard.demeterDashboardGuid] ?? dashboard.name}
                                                    className={styles.dashboard_customization_tab_text_input}
                                                    handleTextChange={(value) => {
                                                        setDashboardNameEdits((previous) => ({
                                                            ...previous,
                                                            [dashboard.demeterDashboardGuid]: value,
                                                        }));
                                                    }}
                                                    handleBlur={(value) => {
                                                        if (value === dashboard.name) {
                                                            return;
                                                        }
                                                        handleDashboardLabelLoseFocus(dashboard.demeterDashboardGuid, value);
                                                    }}
                                                    track={{
                                                        eventCategory: 'Dashboard',
                                                        eventAction: 'DashboardNameChanged',
                                                        eventValue: dashboardNameEdits[dashboard.demeterDashboardGuid] ?? dashboard.name,
                                                    }}
                                                />
                                            )
                                        }
                                        inlineTabKey={dashboard.demeterDashboardGuid}
                                        tabRightSide={
                                            isInEditMode ? (
                                                <IconButton
                                                    className={styles.dashboard_customization_tab_icon_button}
                                                    handleClick={() => handleDeleteDashboardTabButtonClicked(dashboard)}
                                                    tooltip={translations.dashboardCustomization.tooltips.removeDashboard}
                                                    track={{ eventCategory: 'Dashboard', eventAction: 'DeleteDashboardClicked', eventValue: dashboard.name }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <> </>
                                            )
                                        }
                                    >
                                        {isListDemeterDashboardsResponseLoading || triggerRefreshListDemeterDashboardsResponse ? (
                                            <CardSkeleton />
                                        ) : (
                                            <DashboardContainer
                                                dashboard={dashboard}
                                                pageElements={pageElements}
                                                isInEditMode={isInEditMode}
                                                updateUpsertDashboardRequestFromContainer={updateUpsertDashboardRequestFromContainer}
                                                setUpsertDemeterDashboardRequest={setUpsertDemeterDashboardRequest}
                                            />
                                        )}
                                    </InlineTab>
                                ))}
                            </InlineTabs>
                        </div>
                    </div>
                )}
            </div>

            <MenuSettingsModal
                open={isMenuSettingsModalOpen}
                tableDefinitionType={DemeterTableDefinitionType.UsersTable}
                handleClose={handleIsMenuSettingsModalOpen}
                track={{ eventCategory: 'Dashboard', eventAction: 'MenuSettingsModal' }}
            />
            <ConfirmModal
                header={translations.dashboardCustomization.titles.deleteDashboard}
                message={translations.dashboardCustomization.messages.confirmDeleteDashboard}
                showModal={openConfirmDeleteDashboardModal}
                handleConfirm={confirmDeleteDashboard}
                handleCancel={() => {
                    setOpenConfirmDeleteDashboardModal(false);
                    setDeleteDemeterDashboardRequest(undefined);
                }}
                track={{ eventCategory: 'Dashboard', eventAction: 'DeleteDashboardModal' }}
            />
            <ConfirmModal
                header={translations.dashboardCustomization.titles.resetDashboardModal}
                message={translations.dashboardCustomization.messages.confirmResetDashboard}
                showModal={openConfirmResetDashboardModal}
                handleConfirm={confirmResetDashboard}
                handleCancel={() => {
                    setOpenConfirmResetDashboardModal(false);
                }}
                track={{ eventCategory: 'Dashboard', eventAction: 'ResetDashboardModal' }}
            />
        </div>
    );
};

export default DashboardCustomizationPage;
